'use client';

import { TiProviderHero } from '@hyphengroup/cms-components';
import { GradientBackground } from '../../components-internal/GradientBackground';
import type { CmsTiProviderHeroComponent } from '$/(cms)/_modules/cmsService/cmsServiceGql/mapper/components/TiProviderHero';
import type { PageHeaderBackground } from '$/(cms)/_modules/cmsService/types';
import type { CmsPlanTierSelectorComponent } from '$/(cms)/_modules/cmsService/types/components';
import { PageContentContainer } from '$/components/PageContentContainer';
import { useClientContext } from '$/modules/context/client';

type TiProviderHeroWrapperProps = {
  hero: CmsTiProviderHeroComponent | undefined;
  // Provider is owned by M3 (PlanTierSelector) per CMS-1724 — PageContent
  // extracts it and passes it in. The hero no longer carries its own provider.
  provider: CmsPlanTierSelectorComponent['provider'] | undefined;
  headerBackground?: PageHeaderBackground;
};

/**
 * Brand-gated wrapper for the TI Provider Hero block. The block ships only on
 * SingSaver (SG) and MoneyHero (HK) per CMS-1639 — money101 / moneymax must
 * render nothing. The hero is hoisted out of BlockMapper and rendered above
 * the default breadcrumb / page header by PageContent.tsx (see
 * FlexibleHeroBlockWrapper precedent).
 *
 * Takes a single optional hero (not an array): the component renders an
 * `<h1>`, and the page's document outline allows exactly one. If multiple
 * hero entries land in Strapi the caller passes the first.
 *
 * Visibility + malformed-entry filtering (missing providerImage) happens
 * upstream in PageContent so the same predicate drives both the render
 * decision and the `shouldRenderDefaultHero` suppression.
 */
export const TiProviderHeroWrapper = ({
  hero,
  provider,
  headerBackground,
}: TiProviderHeroWrapperProps) => {
  const { brand } = useClientContext();

  if (brand !== 'singsaver' && brand !== 'moneyhero') {
    return null;
  }

  if (!hero || !provider) {
    return null;
  }

  return (
    <>
      <GradientBackground {...headerBackground} />
      <PageContentContainer className="md:pt-spacing/padding/y">
        <TiProviderHero
          provider={provider}
          title={hero.title}
          description={hero.description}
          promoCode={hero.promoCode}
          promoCodeEnabled={hero.promoCodeEnabled}
        />
      </PageContentContainer>
    </>
  );
};
